import { Profile, Buildings, Clock } from 'iconsax-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  selectUserInfo,
  selectSelectedWorkStation,
  selectSelectedTeam,
} from '@/store/api/Users/Users.selector'
import type { BatchValidationResult } from '@/utils/batchFileValidation'
import { BATCH_REQUIRED_FIELDS } from '@/utils/batchFileValidation'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

interface BatchSearchSummaryStepProps {
  validationResult: BatchValidationResult
  editedData: Record<string, string>
  isTermsAccepted: boolean
  onTermsChange: (accepted: boolean) => void
  dynamicValidationStatus?: {
    hasErrors: boolean
    totalErrors: number
    validRows: number
    validRowIndexes?: number[]
  } | null
}

interface FinalBatchData {
  metadata: {
    submittedAt: string
    submittedBy: {
      id: string
      name: string
      email: string
    }
    workspace: {
      id: string
      name: string
    }
    team: {
      id: string
      name: string
    }
    totalRows: number
    validRows: number
  }
  subjects: any[]
}

export const BatchSearchSummaryStep = ({
  validationResult,
  editedData,
  isTermsAccepted,
  onTermsChange,
  dynamicValidationStatus,
}: BatchSearchSummaryStepProps) => {
  const { t } = useTranslation()

  const userInfo = useSelector(selectUserInfo)
  const selectedWorkstation = useSelector(selectSelectedWorkStation)
  const selectedTeam = useSelector(selectSelectedTeam)

  const handleCheckboxChange = (newChecked: boolean) => {
    onTermsChange(newChecked)
  }

  const finalData = useMemo(() => {
    const processedSubjects = validationResult.detailedResults
      .filter((result) => {
        // Use dynamic validation status if available and edits were made
        if (
          dynamicValidationStatus?.validRowIndexes &&
          Object.keys(editedData).length > 0
        ) {
          return dynamicValidationStatus.validRowIndexes.includes(
            result.rowIndex,
          )
        }
        // Otherwise use original validation
        return result.isValid
      })
      .map((result) => {
        const processedData: any = { ...result.data }

        Object.keys(editedData).forEach((key) => {
          const dashIndex = key.indexOf('-')
          if (dashIndex === -1) return

          const rowIndex = key.substring(0, dashIndex)
          const field = key.substring(dashIndex + 1)

          if (parseInt(rowIndex, 10) === result.rowIndex) {
            processedData[field] = editedData[key]
          }
        })

        return processedData
      })

    const finalBatchData: FinalBatchData = {
      metadata: {
        submittedAt: new Date().toISOString(),
        submittedBy: {
          id: userInfo.userId || '',
          name: `${userInfo.firstname} ${userInfo.lastname}`,
          email: userInfo.email || '',
        },
        workspace: {
          id: selectedWorkstation?._id || '',
          name: selectedWorkstation?.name || 'All Workspaces',
        },
        team: {
          id: selectedTeam?._id || '',
          name: selectedTeam?.name || 'Default Team',
        },
        totalRows: validationResult.detailedResults.length,
        validRows: processedSubjects.length,
      },
      subjects: processedSubjects,
    }

    return finalBatchData
  }, [
    validationResult,
    editedData,
    userInfo,
    selectedWorkstation,
    selectedTeam,
    dynamicValidationStatus,
  ])

  const getFieldSummary = () => {
    const fieldCounts: Record<string, { filled: number; total: number }> = {}

    finalData.subjects.forEach((subject: any) => {
      Object.keys(subject).forEach((field) => {
        // Exclude "subject" field from field completion
        if (field === 'subject') {
          return
        }

        if (!fieldCounts[field]) {
          fieldCounts[field] = { filled: 0, total: 0 }
        }
        fieldCounts[field].total++
        if (subject[field] && String(subject[field]).trim() !== '') {
          fieldCounts[field].filled++
        }
      })
    })

    return fieldCounts
  }

  const fieldSummary = getFieldSummary()

  const formatFieldName = (field: string) => {
    return field
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <div className="flex flex-col h-full w-full gap-6 overflow-auto">
      {/* Header */}
      <Card className="p-6 flex-shrink-0">
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold text-neutralColor">
            {t(
              'dashboard.batchSearch.summaryAndSubmission',
              'Summary & Submission',
            )}
          </h2>
          <p className="text-neutralColor60">
            {t(
              'dashboard.batchSearch.summaryDescription',
              'Review your batch search request before submission. This will process all valid subjects and create individual search reports.',
            )}
          </p>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
        {/* Summary Statistics */}
        <Card className="p-6 ">
          <h3 className="text-lg font-semibold text-neutralColor mb-4">
            {t('dashboard.batchSearch.summary', 'Summary')}
          </h3>

          <div className="space-y-6 max-h-[30rem] overflow-y-auto pr-2">
            <div className="flex items-center gap-3">
              <Profile size="20" color={weCheckPalette.primary} />
              <div>
                <p className="text-sm text-neutralColor60">
                  {t('dashboard.batchSearch.totalSubjects', 'Total Subjects')}
                </p>
                <p className="text-lg font-semibold text-neutralColor">
                  {finalData.metadata.validRows}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Profile size="20" color={weCheckPalette.primary} />
              <div>
                <p className="text-sm text-neutralColor60">
                  {t('dashboard.batchSearch.requester', 'Requester')}
                </p>
                <p className="text-lg font-semibold text-neutralColor">
                  {finalData.metadata.submittedBy.name}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Buildings size="20" color={weCheckPalette.primary} />
              <div>
                <p className="text-sm text-neutralColor60">
                  {t('dashboard.batchSearch.workspace', 'Workspace')}
                </p>
                <p className="text-lg font-semibold text-neutralColor">
                  {finalData.metadata.workspace.name}
                </p>
              </div>
            </div>

            {finalData.metadata.team.name && (
              <div className="flex items-center gap-3">
                <Buildings size="20" color={weCheckPalette.primary} />
                <div>
                  <p className="text-sm text-neutralColor60">
                    {t('dashboard.batchSearch.team', 'Team')}
                  </p>
                  <p className="text-lg font-semibold text-neutralColor">
                    {finalData.metadata.team.name}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Clock size="20" color={weCheckPalette.primary} />
              <div>
                <p className="text-sm text-neutralColor60">
                  {t('dashboard.batchSearch.submissionTime', 'Submission Time')}
                </p>
                <p className="text-lg font-semibold text-neutralColor">
                  {new Date(finalData.metadata.submittedAt).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Disclaimer about changing workspace/team */}
            <div className="mt-6 p-3 bg-baseColor border-l-4 border-l-dynamicBlue rounded-r">
              <p className="text-sm text-neutralColor60">
                💡 {t('dashboard.batchSearch.changeAssignment')}
              </p>
            </div>
          </div>
        </Card>

        {/* Field Completion */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutralColor mb-4">
            {t('dashboard.batchSearch.fieldCompletion', 'Field Completion')}
          </h3>

          <div className="space-y-3 max-h-[30rem] overflow-y-auto pr-2">
            {Object.entries(fieldSummary).map(([field, stats]) => {
              const percentage = Math.round((stats.filled / stats.total) * 100)
              const isRequired = BATCH_REQUIRED_FIELDS.includes(field)

              return (
                <div key={field} className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutralColor flex items-center gap-1">
                      {formatFieldName(field)}
                      {isRequired && (
                        <span className="text-xs text-dynamicRed">*</span>
                      )}
                    </span>
                    <span className="text-xs text-neutralColor60">
                      {stats.filled}/{stats.total} ({percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-neutralColor30 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        percentage === 100
                          ? 'bg-dynamicGreen'
                          : percentage >= 80
                            ? 'bg-dynamicBlue'
                            : percentage >= 60
                              ? 'bg-dynamicYellow'
                              : 'bg-dynamicRed'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      </div>

      {/* Terms Agreement and Action Buttons */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-4">
          <div className="flex gap-2 items-center">
            <Checkbox
              className="w-5 h-5 rounded-md"
              checked={isTermsAccepted}
              onCheckedChange={handleCheckboxChange}
            />

            <p className="text-neutralColor text-textNormal text-sm font-medium tracking-tight">
              {t('dashboard.newSearch.legalDisclaimer.checkingBox')}
              <span
                className="text-linkColor"
                role="button"
                onClick={() =>
                  window.open(
                    t('dashboard.common.termsAndConditionsLink'),
                    '_blank',
                  )
                }
                tabIndex={0}
              >
                &nbsp;
                {t('dashboard.newSearch.legalDisclaimer.termsAndConditions')}
              </span>
              {t('dashboard.newSearch.legalDisclaimer.and')}&nbsp;
              <span
                className="text-linkColor"
                role="button"
                onClick={() =>
                  window.open(t('dashboard.common.privacyPolicyLink'), '_blank')
                }
                tabIndex={0}
              >
                {t('dashboard.newSearch.legalDisclaimer.privacyPolicy')}
              </span>
              . 1 {t('dashboard.batchSearch.creditDeductedPerSubject')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
